import React, { useState, useCallback, useEffect, useRef } from 'react';
import './ColumnConfigurator.css';
import { getColumnDefinitions, reorderArray } from '../utils/columnConfig';

/**
 * Modal component for configuring table columns
 * Supports drag-and-drop reordering and visibility toggles
 */
function ColumnConfigurator({
  isOpen,
  onClose,
  tableName,
  tableTitle,
  currentConfig,
  onApply
}) {
  // Local state for editing before applying
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [columnOrder, setColumnOrder] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Touch drag state
  const [touchDragIndex, setTouchDragIndex] = useState(null);
  const touchStartY = useRef(null);
  const itemRefs = useRef({});

  // Get column definitions for this table
  const columnDefs = getColumnDefinitions(tableName);

  // Initialize local state from current config
  useEffect(() => {
    if (isOpen && currentConfig) {
      setVisibleColumns([...currentConfig.visibleColumns]);
      setColumnOrder([...currentConfig.columnOrder]);
    }
  }, [isOpen, currentConfig]);

  // Toggle column visibility
  const handleToggleColumn = useCallback((key) => {
    const colDef = columnDefs.find(c => c.key === key);
    if (colDef && !colDef.canHide) return; // Can't hide required columns

    setVisibleColumns(prev => {
      if (prev.includes(key)) {
        return prev.filter(k => k !== key);
      } else {
        return [...prev, key];
      }
    });
  }, [columnDefs]);

  // Drag and drop handlers (desktop)
  const handleDragStart = useCallback((e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    // Add a slight delay to show drag effect
    requestAnimationFrame(() => {
      e.target.classList.add('dragging');
    });
  }, []);

  const handleDragEnd = useCallback((e) => {
    e.target.classList.remove('dragging');
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  const handleDragOver = useCallback((e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedIndex !== null && index !== draggedIndex) {
      setDragOverIndex(index);
    }
  }, [draggedIndex]);

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback((e, targetIndex) => {
    e.preventDefault();
    const sourceIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (sourceIndex !== targetIndex) {
      setColumnOrder(prev => reorderArray(prev, sourceIndex, targetIndex));
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  // Touch handlers for mobile drag-and-drop
  const handleTouchStart = useCallback((e, index) => {
    touchStartY.current = e.touches[0].clientY;
    setTouchDragIndex(index);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (touchDragIndex === null) return;

    const touchY = e.touches[0].clientY;
    const itemKeys = columnOrder;

    // Find which item we're over
    for (let i = 0; i < itemKeys.length; i++) {
      const ref = itemRefs.current[itemKeys[i]];
      if (ref) {
        const rect = ref.getBoundingClientRect();
        if (touchY >= rect.top && touchY <= rect.bottom) {
          if (i !== touchDragIndex && i !== dragOverIndex) {
            setDragOverIndex(i);
          }
          break;
        }
      }
    }
  }, [touchDragIndex, columnOrder, dragOverIndex]);

  const handleTouchEnd = useCallback(() => {
    if (touchDragIndex !== null && dragOverIndex !== null && touchDragIndex !== dragOverIndex) {
      setColumnOrder(prev => reorderArray(prev, touchDragIndex, dragOverIndex));
    }
    setTouchDragIndex(null);
    setDragOverIndex(null);
    touchStartY.current = null;
  }, [touchDragIndex, dragOverIndex]);

  // Reset to default order (all columns visible, original order)
  const handleReset = useCallback(() => {
    const defaultOrder = columnDefs.map(c => c.key);
    setColumnOrder(defaultOrder);
    setVisibleColumns(defaultOrder);
  }, [columnDefs]);

  // Apply changes
  const handleApply = useCallback(() => {
    onApply(visibleColumns, columnOrder);
    onClose();
  }, [visibleColumns, columnOrder, onApply, onClose]);

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Build ordered list of columns based on columnOrder
  const orderedColumns = columnOrder
    .map(key => columnDefs.find(c => c.key === key))
    .filter(Boolean);

  return (
    <div className="column-configurator-overlay" onClick={onClose}>
      <div className="column-configurator-modal" onClick={e => e.stopPropagation()}>
        <div className="configurator-header">
          <h3>Configure {tableTitle} Columns</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="configurator-instructions">
          Drag to reorder. Check to show column.
        </div>

        <div
          className="columns-list"
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {orderedColumns.map((col, index) => {
            const isVisible = visibleColumns.includes(col.key);
            const isDragging = draggedIndex === index || touchDragIndex === index;
            const isDragOver = dragOverIndex === index;

            return (
              <div
                key={col.key}
                ref={el => itemRefs.current[col.key] = el}
                className={`column-item ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''} ${!isVisible ? 'hidden-column' : ''}`}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                onTouchStart={(e) => handleTouchStart(e, index)}
              >
                <span className="drag-handle">&#9776;</span>
                <label className="column-checkbox">
                  <input
                    type="checkbox"
                    checked={isVisible}
                    onChange={() => handleToggleColumn(col.key)}
                    disabled={!col.canHide}
                  />
                  <span className="checkmark"></span>
                </label>
                <span className="column-label">{col.label}</span>
                {!col.canHide && <span className="required-badge">Required</span>}
              </div>
            );
          })}
        </div>

        <div className="configurator-footer">
          <button className="reset-btn" onClick={handleReset}>
            Reset to Default
          </button>
          <div className="footer-actions">
            <button className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button className="apply-btn" onClick={handleApply}>
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ColumnConfigurator;
